import { ApolloClient, InMemoryCache, type DocumentNode, type OperationVariables, type NormalizedCacheObject } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
export { ApolloProvider as Provider } from "@apollo/client/react";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";
import type { Preview } from "@i18next/site";
import { LocalStorageWrapper, persistCache } from "apollo3-cache-persist";
import { defaultDataIdFromObject, urlDeleteRefresh } from "../utils";
import { ApolloLink } from "@apollo/client/link/core";
import { HttpLink } from "@apollo/client/link/http";
import { onError } from "@apollo/client/link/error";
import { Logger } from "@module/logger";

export { useQuery } from "@apollo/client/react/hooks";
export { type NormalizedCacheObject, gql } from "@apollo/client/core";

const SSR = import.meta.env.SSR;
const PROD = import.meta.env.PROD;
const GRAPHQL_ENDPOINT = SSR ? import.meta.env.GRAPHQL_ENDPOINT : import.meta.env.PUBLIC_ENDPOINT;
const GRAPHQL_TOKEN = SSR ? import.meta.env.GRAPHQL_TOKEN : import.meta.env.PUBLIC_TOKEN;

const logger = new Logger("Apollo SSR");

if(!PROD) {
  loadDevMessages();
  loadErrorMessages();
}

const cache = new InMemoryCache({
  resultCacheMaxSize: Math.pow(2, 15), // Setting max cache size
  typePolicies: {
    RetourType: {
      fields: {
        redirectDestUrl: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, urlDeleteRefresh(incoming));
          },
        }
      }
    }
  },
  dataIdFromObject(_obj) {
    switch (_obj.__typename) {
      case 'RetourType':
        let id = (typeof _obj.redirectDestUrl === "string" && !!_obj.enabled) ? `${defaultDataIdFromObject(_obj)}-${_obj.redirectDestUrl}` : undefined;
        return id;
      default: return defaultDataIdFromObject(_obj);
    }
  }
});

if(!SSR) {
  // 使用 cache.restore(initialState); 持久化会被覆盖
  // logger.log("persistCache");
  await persistCache({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
    maxSize: false,
  });
}

interface createApolloLink {
  (
    props: {
      url?: string | URL;
      search?: Record<string, string | undefined>;
      headers?: Record<string, string | undefined>;
    }
  ): ApolloLink
}

export const createApolloLink:createApolloLink = ({
  url:endpoint = GRAPHQL_ENDPOINT,
  search = {},
  headers = {}
}) => {
  const url = (typeof endpoint === "string") ? new URL(endpoint) : endpoint;

  Object.entries(search).forEach(([key, value]) => {
    if (value)
      url.searchParams.set(key, value);
  });

  const logLink = new ApolloLink((operation, forward) => {
    logger.log(`Starting request for ${operation.operationName}`);
    logger.log(`Request: \r\n`, operation);
  
    return forward(operation).map((response) => {
      logger.log(`Received response for ${operation.operationName}`);
      logger.log(`Response: \r\n`, response);
      return response;
    });
  });

  const httpLink = new HttpLink({
    uri: url.toString(),
    fetchOptions: { // 提供给 fetch 的配置选项，如 mode、cache、redirect 等。可以在这里设置 no-cors、cors、same-origin 等 CORS 模式。
      mode: SSR ? 'no-cors' : 'cors', // 可选，控制 CORS 请求的模式
    },
    credentials: 'same-origin', // 'omit' 用于控制是否在请求中发送 Cookies 和其他凭证
  });

  const authLink = setContext((_, { headers:_headers }) => {
    return {
      headers: {
        ..._headers,
        ...headers,
        // 'Cache-Control': 'no-cache',
        authorization: `Bearer ${GRAPHQL_TOKEN}`,
      },
    };
  });

  const errorLink = onError((ErrorResponse) => {
    const { graphQLErrors, networkError, response, operation, forward } = ErrorResponse;
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        logger.error(`[GraphQL error]: `, error);
      });
    }
    if (networkError)
      logger.error(`[Network error]: `, networkError);
  });
  return !PROD ? logLink.concat(errorLink.concat(authLink.concat(httpLink))) : errorLink.concat(authLink.concat(httpLink));
}

interface createApolloClient {
  (props: {
    // initialState?: NormalizedCacheObject | null;
    headers?: Preview;
    // cache?: InMemoryCache;
  }):ApolloClient<NormalizedCacheObject>
};
const createApolloClient:createApolloClient = ({
  // initialState = null, 
  headers = {},
  // cache = new InMemoryCache()
}) => {
  const {token, ..._headers} = headers;
  const apolloLink = createApolloLink({
    search: { token: token },
    headers: _headers,
  });

  return new ApolloClient({
    ssrMode: SSR,
    ssrForceFetchDelay: 60000, // 60秒内如果有多个相同查询，则只发送一个请求
    link: apolloLink,
    cache: cache,
    defaultOptions: {
      query: {
        fetchPolicy: "cache-first"
      },
      watchQuery: {
        fetchPolicy: "cache-first",
        // nextFetchPolicy: "network-only",
        // errorPolicy: isProd ? 'ignore' : 'all',
        errorPolicy: 'all',
        // pollInterval: 60 * 1000,
        // skipPollAttempt: true,
        refetchWritePolicy: "overwrite",
      }
    },
  });
}


let apolloClient: ApolloClient<NormalizedCacheObject>;

interface initializeApolloProps {
  initialState?: NormalizedCacheObject | null;
  headers?: Preview;
}
export function initializeApollo({
  initialState = null,
  headers = {}
}: initializeApolloProps): ApolloClient<NormalizedCacheObject> {
  const _apolloClient = apolloClient ?? createApolloClient({headers});
  
  if (initialState) {
    // cache.restore(initialState);
    _apolloClient.cache.restore(initialState);
  }

  // 如果是 SSR 模式，直接返回 ApolloClient 实例
  if (SSR) return _apolloClient;

  // 在客户端创建单例
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function useApollo(initialState?: NormalizedCacheObject | null, headers: Preview | undefined = {}): ApolloClient<NormalizedCacheObject> {
  return initializeApollo({initialState, headers});
}